import { For } from 'solid-js';
import { useTheme } from '../theme/ThemeProvider';
import { useVim } from '../vim/VimProvider';
import type { ThemePreset, ThemeMode } from '../theme/types';

export function ThemeSwitcher() {
  const theme = useTheme();
  const vim = useVim();

  const handlePresetChange = (e: Event) => {
    const target = e.target as HTMLSelectElement;
    theme.setPreset(target.value as ThemePreset);
  };

  const handleModeChange = (newMode: ThemeMode) => {
    theme.setMode(newMode);
  };

  const handleVimChange = (enabled: boolean) => {
    vim.setEnabled(enabled);
    if (enabled) {
      vim.setMode('normal');
    }
  };

  return (
    <div class="theme-switcher" role="region" aria-label="Theme settings">
      <div class="theme-switcher__section">
        <label for="theme-preset" class="theme-switcher__label">
          Style
        </label>
        <select
          id="theme-preset"
          class="theme-switcher__select"
          value={theme.preset()}
          onChange={handlePresetChange}
          aria-label="Select visual theme"
        >
          <For each={theme.availablePresets}>
            {preset => (
              <option value={preset.name} title={preset.description}>
                {preset.label}
              </option>
            )}
          </For>
        </select>
      </div>

      <div class="theme-switcher__section">
        <span class="theme-switcher__label">Mode</span>
        <div class="theme-switcher__toggle" role="radiogroup" aria-label="Color mode">
          <button
            type="button"
            class="theme-switcher__mode-btn"
            classList={{ 'is-active': theme.mode() === 'light' }}
            onClick={() => handleModeChange('light')}
            role="radio"
            aria-checked={theme.mode() === 'light'}
            aria-label="Light mode"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="5" />
              <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
            </svg>
          </button>
          <button
            type="button"
            class="theme-switcher__mode-btn"
            classList={{ 'is-active': theme.mode() === 'dark' }}
            onClick={() => handleModeChange('dark')}
            role="radio"
            aria-checked={theme.mode() === 'dark'}
            aria-label="Dark mode"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          </button>
        </div>
      </div>

      <div class="theme-switcher__section">
        <span class="theme-switcher__label">Vim</span>
        <div class="theme-switcher__toggle" role="radiogroup" aria-label="Vim navigation mode">
          <button
            type="button"
            class="theme-switcher__mode-btn"
            classList={{ 'is-active': !vim.state().enabled }}
            onClick={() => handleVimChange(false)}
            role="radio"
            aria-checked={!vim.state().enabled}
            aria-label="Vim off"
            title="Disable vim navigation"
          >
            Off
          </button>
          <button
            type="button"
            class="theme-switcher__mode-btn"
            classList={{ 'is-active': vim.state().enabled }}
            onClick={() => handleVimChange(true)}
            role="radio"
            aria-checked={vim.state().enabled}
            aria-label="Vim on"
            title="Enable vim navigation"
          >
            On
          </button>
        </div>
      </div>
    </div>
  );
}
